import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import Header from './components/common/Header';
import Dashboard from './components/Dashboard';
// Active components
import SituationAnalysis from './components/SituationAnalysis';
import AboutPage from './components/AboutPage';
import CrisisView from './components/CrisisView';
import DecisionInterface from './components/DecisionInterface';
import CrisisAlerts from './components/CrisisAlerts';
import DataMonitor from './components/DataMonitor';
import HistoricalTimelineAnalysisMapbox from './components/HistoricalTimelineAnalysisMapbox';
import AuditTrail from './components/audit-trail/AuditTrail';
import LeafletMap from './components/LeafletMap';
import FeedbackDashboard from './components/feedback/FeedbackDashboard';
import FeedbackWidget from './components/feedback/FeedbackWidget';
import ProductDetails from './components/ProductDetails';

// ARCHIVED - Moved to _archived directories
// import AgentDashboard from './components/_archived/AgentDashboard';
// import FieldResponse from './components/_archived/FieldResponse';
// import FieldResponseLegacy from './components/_archived/FieldResponseLegacy';
// import ResearchBriefing from './components/_archived/ResearchBriefing';
// import UserResearchPage from './pages/_archived/UserResearchPage';
// import WorkshopFrameworkPage from './pages/_archived/WorkshopFrameworkPage';
// import VisualWorkshopPage from './pages/_archived/VisualWorkshopPage';
// import UnifiedResearchPage from './pages/_archived/UnifiedResearchPage';
// import SilviaWorkshopPrototypes from './components/_archived/workshop/SilviaWorkshopPrototypes';
import ReportPage from './pages/ReportPage';
import { OnboardingProvider } from './components/onboarding/OnboardingProvider';
import OnboardingOrchestrator from './components/onboarding/OnboardingOrchestrator';
// import ContextualHelp, { withContextualHelp } from './components/onboarding/ContextualHelp';
import LandingPage from './components/LandingPage';
// ARCHIVED: import { SurveyGenerator, SurveyManagement, AnalyticsDashboard } from './components/survey';
import { Crisis } from './types';
import { crisisData } from './data/crisisData';
import { USER_ROLES } from './data/onboardingData';
import './i18n';
import 'react-image-crop/dist/ReactCrop.css';
import './styles/feedback-animations.css';
import './styles/onboarding-animations.css';
import './styles/swiss-theme.css';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
// ARCHIVED: import SwissThemeDemo from './components/theme/SwissThemeDemo';
import RouteThemeWrapper from './components/theme/RouteThemeWrapper';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
// ARCHIVED: import DemoDashboard from './pages/_archived/DemoDashboard';
import ProtectedRoute from './components/auth/ProtectedRoute';
import PasswordProtectedRoute from './components/auth/PasswordProtectedRoute';
import { PasswordPrompt } from './components/auth/PasswordPrompt';
import { useAuth } from './contexts/AuthContext';
import {
  WireframeOverview,
  WireframeDetection,
  WireframeAnalysis,
  WireframeOperations,
  WireframeCashAssistance,
  WireframeKnowledge,
  WireframeCrisisMap,
  WireframePresentation,
} from './components/wireframes';
import { WireframeWrapper } from './components/wireframes/WireframeWrapper';

interface User {
  id: string;
  name: string;
  role: string;
  avatar: string;
  experience?: 'new' | 'experienced' | 'expert';
  isFirstLogin?: boolean;
}

// Simple Layout Component for universal Header
const AppLayout = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const isWireframePage = location.pathname.startsWith('/wireframes');
  const isLandingPage = location.pathname === '/';
  const isDemoPage = location.pathname === '/demo';

  return (
    <div className="min-h-screen bg-sand-50 text-ink-900 overflow-x-hidden">
      {/* Header hidden from all pages */}

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* NRC-style Footer - Hidden for wireframe pages, landing page, and demo page */}
      {!isWireframePage && !isLandingPage && !isDemoPage && <div>
        <footer className="border-t-2 border-ink-200 mt-24 bg-white">
          <div className="container-grid py-12">
            <div className="grid-12">
              {/* Main Footer Content */}
              <div className="col-span-12 lg:col-span-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Organization Info */}
                  <div>
                    <h3 className="text-sm font-sans font-semibold text-ink-900 mb-4">Norwegian Refugee Council</h3>
                    <address className="text-xs font-sans text-ink-600 not-italic leading-relaxed">
                      Prinsens gate 2<br />
                      0152 Oslo<br />
                      Norway
                    </address>
                  </div>

                  {/* Contact Information */}
                  <div>
                    <h4 className="text-sm font-sans font-semibold text-ink-900 mb-4">Contact</h4>
                    <a href="mailto:clear@nrc.no" className="text-sm font-sans text-accent-500 hover:text-accent-600 transition-colors">clear@nrc.no</a>
                  </div>
                </div>
              </div>

              {/* Impact Statement */}
              <div className="col-span-12 lg:col-span-4 flex items-center justify-center lg:justify-end">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-24 h-24 border-4 border-accent-500 mb-4">
                    <span className="text-2xl font-bold text-accent-500">90%</span>
                  </div>
                  <p className="text-xs font-sans text-ink-600 max-w-[200px]">
                    Over 90 per cent of our global revenue goes directly to fulfil our purpose.
                  </p>
                </div>
              </div>

              {/* Bottom Section */}
              <div className="col-span-12 border-t border-ink-200 pt-6 mt-8">
                <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
                  {/* Copyright */}
                  <div className="text-center lg:text-left">
                    <p className="text-xs font-sans text-ink-500">
                      © {new Date().getFullYear()} CLEAR — Crisis Learning, Early-warning, Anticipation, and Response
                    </p>
                    <p className="text-xs font-sans text-ink-500">
                      NRC © 1996-{new Date().getFullYear()}
                    </p>
                  </div>

                  {/* Links */}
                  <nav className="flex flex-wrap justify-center gap-4">
                    <a href="#" className="text-xs font-sans text-ink-600 hover:text-accent-500 transition-colors">Privacy policy</a>
                    <a href="#" className="text-xs font-sans text-ink-600 hover:text-accent-500 transition-colors">About cookies</a>
                    <a href="https://www.nrc.no" target="_blank" rel="noopener noreferrer" className="text-xs font-sans text-accent-500 hover:text-accent-600 transition-colors">
                      Norwegian Refugee Council
                    </a>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>}

      {/* Feedback Widget - Persistent across all pages except landing page and demo page */}
      {location.pathname !== '/' && location.pathname !== '/demo' && !isWireframePage && <FeedbackWidget />}
    </div>
  );
};

// Crisis Detail Page Wrapper
const CrisisDetailPage = () => {
  const { crisisId } = useParams<{ crisisId: string }>();
  const navigate = useNavigate();
  const [user] = React.useState<User>({ 
    id: 'user-002',
    name: 'Sarah Mitchell', 
    role: 'Country Director', 
    avatar: '/api/placeholder/32/32' 
  });

  // Find the crisis by ID
  const crisis = crisisData.find(c => c.id === crisisId);

  if (!crisis) {
    return (
      <div className="container-grid py-12">
        <div className="grid-12">
          <div className="col-span-12 text-center">
            <h1 className="text-2xl font-bold mb-4">Crisis Not Found</h1>
            <p className="text-gray-600 mb-6">The crisis you're looking for doesn't exist.</p>
            <button 
              onClick={() => navigate('/dashboard')}
              className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition-colors"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <RouteThemeWrapper>
      <CrisisView
        crisis={crisis}
        onMakeDecision={() => navigate(`/crisis/${crisisId}/decision`)}
        onBack={() => navigate('/dashboard')}
      />
    </RouteThemeWrapper>
  );
};

// Decision Interface Page Wrapper  
const DecisionPage = () => {
  const { crisisId } = useParams<{ crisisId: string }>();
  const navigate = useNavigate();
  const [user] = React.useState<User>({ 
    id: 'user-003',
    name: 'Sarah Mitchell', 
    role: 'Country Director', 
    avatar: '/api/placeholder/32/32' 
  });

  const crisis = crisisData.find(c => c.id === crisisId);

  if (!crisis) {
    return (
      <div className="container-grid py-12">
        <div className="grid-12">
          <div className="col-span-12 text-center">
            <h1 className="text-2xl font-bold mb-4">Crisis Not Found</h1>
            <button onClick={() => navigate('/dashboard')} className="bg-orange-500 text-white px-6 py-3 rounded-lg">
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <RouteThemeWrapper>
      <DecisionInterface
        crisis={crisis}
        user={user}
        onBack={() => navigate(`/crisis/${crisisId}`)}
        onDecisionMade={(decision: any) => {
          console.log('Decision made:', decision);
          navigate(`/crisis/${crisisId}`);
        }}
      />
    </RouteThemeWrapper>
  );
};

// Page wrapper component (for demo pages with theme support)
const PageWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <RouteThemeWrapper>
      <div className="container-grid py-12">
        <div className="grid-12">
          <div className="col-span-12">
            {children}
          </div>
        </div>
      </div>
    </RouteThemeWrapper>
  );
};

// Landing page wrapper with navigation and password protection
const LandingPageWrapper = () => {
  const navigate = useNavigate();
  const { isHomepageUnlocked, unlockHomepage } = useAuth();

  if (!isHomepageUnlocked) {
    return <PasswordPrompt onSuccess={() => unlockHomepage('CLEAR')} />;
  }

  return (
    <LandingPage
      onGetStarted={() => navigate('/dashboard')}
      onLogin={() => navigate('/dashboard')}
    />
  );
};

// Dashboard wrapper with crisis selection
const DashboardWrapper = () => {
  const navigate = useNavigate();

  const handleCrisisSelect = (crisis: Crisis | any) => {
    navigate(`/crisis/${crisis.id}`);
  };

  const handleMakeDecision = (crisis: Crisis | any) => {
    navigate(`/crisis/${crisis.id}/decision`);
  };

  return (
    <PageWrapper>
      <Dashboard onCrisisSelect={handleCrisisSelect} onMakeDecision={handleMakeDecision} />
    </PageWrapper>
  );
};

// Onboarding page wrapper
const OnboardingPageWrapper = () => {
  return (
    <div className="min-h-screen bg-sand-50">
      <div className="container-grid py-12">
        <div className="grid-12">
          <div className="col-span-12">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-ink-900 mb-4">Welcome to CLEAR</h1>
              <p className="text-ink-600">Let's get you set up with the Crisis Learning, Early-warning, Anticipation, and Response platform</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Onboarding System */}
      <OnboardingOrchestrator
        currentUser={{
          id: 'user-001',
          name: 'Sarah Mitchell',
          role: USER_ROLES.find(r => r.name === 'Country Director') || USER_ROLES[0],
          experience: 'new',
          isFirstLogin: true
        }}
        organizationContext={{
          type: 'UN_Agency',
          size: 'medium',
          regions: ['East Africa', 'Horn of Africa'],
          primaryCrisisTypes: ['Drought', 'Conflict', 'Food Insecurity']
        }}
      />
    </div>
  );
};

// About page wrapper with navigation and password protection
const AboutPageWrapper = () => {
  const navigate = useNavigate();
  const { isHomepageUnlocked, unlockHomepage } = useAuth();

  if (!isHomepageUnlocked) {
    return <PasswordPrompt onSuccess={() => unlockHomepage('CLEAR')} />;
  }

  return (
    <AboutPage
      onHome={() => navigate('/')}
    />
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <OnboardingProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <AppLayout>
              <Routes>
              {/* All pages now use universal Header */}
              {/* Homepage and About - Always CLEAR theme */}
              <Route path="/" element={<Navigate to="/demo" replace />} />
              {/* Demo page - Wireframes Overview (password protected) */}
              <Route path="/demo" element={<PasswordProtectedRoute><WireframeWrapper><WireframeOverview /></WireframeWrapper></PasswordProtectedRoute>} />
              <Route path="/about" element={<AboutPageWrapper />} />
              <Route path="/product" element={<ProductDetails />} />
              <Route path="/report" element={<PageWrapper><ReportPage /></PageWrapper>} />
              <Route path="/homev1" element={<LandingPageWrapper />} />

              {/* Authentication Routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />

              {/* Core Application Routes */}
            <Route path="/dashboard" element={<ProtectedRoute><DashboardWrapper /></ProtectedRoute>} />
            <Route path="/situation-analysis" element={<ProtectedRoute><PageWrapper><SituationAnalysis /></PageWrapper></ProtectedRoute>} />
            <Route path="/alerts" element={<ProtectedRoute><PageWrapper><CrisisAlerts /></PageWrapper></ProtectedRoute>} />
            <Route path="/data" element={<ProtectedRoute><PageWrapper><DataMonitor /></PageWrapper></ProtectedRoute>} />
            <Route path="/map" element={<ProtectedRoute><PageWrapper><LeafletMap /></PageWrapper></ProtectedRoute>} />
            <Route path="/audit" element={<ProtectedRoute><PageWrapper><AuditTrail /></PageWrapper></ProtectedRoute>} />
            <Route path="/historical" element={<ProtectedRoute><PageWrapper><HistoricalTimelineAnalysisMapbox /></PageWrapper></ProtectedRoute>} />
            <Route path="/feedback-dashboard" element={<ProtectedRoute><PageWrapper><FeedbackDashboard /></PageWrapper></ProtectedRoute>} />

            {/* ARCHIVED ROUTES - Moved to _archived directories
            <Route path="/demos" element={<ProtectedRoute><DemoDashboard /></ProtectedRoute>} />
            <Route path="/research" element={<ProtectedRoute><RouteThemeWrapper><UnifiedResearchPage /></RouteThemeWrapper></ProtectedRoute>} />
            <Route path="/research-legacy" element={<ProtectedRoute><RouteThemeWrapper><ResearchBriefing /></RouteThemeWrapper></ProtectedRoute>} />
            <Route path="/user-research" element={<ProtectedRoute><RouteThemeWrapper><UserResearchPage /></RouteThemeWrapper></ProtectedRoute>} />
            <Route path="/agent" element={<ProtectedRoute><PageWrapper><AgentDashboard /></PageWrapper></ProtectedRoute>} />
            <Route path="/onboarding" element={<ProtectedRoute><OnboardingPageWrapper /></ProtectedRoute>} />
            <Route path="/field" element={<ProtectedRoute><PageWrapper><FieldResponse /></PageWrapper></ProtectedRoute>} />
            <Route path="/field-legacy" element={<ProtectedRoute><PageWrapper><FieldResponseLegacy /></PageWrapper></ProtectedRoute>} />
            */}

            {/* Protected Crisis Routes */}
            <Route path="/crisis/:crisisId" element={<ProtectedRoute><CrisisDetailPage /></ProtectedRoute>} />
            <Route path="/crisis/:crisisId/decision" element={<ProtectedRoute><DecisionPage /></ProtectedRoute>} />

            {/* ARCHIVED ROUTES - Survey and Workshop moved to _archived
            <Route path="/surveys" element={<ProtectedRoute><PageWrapper><SurveyGenerator /></PageWrapper></ProtectedRoute>} />
            <Route path="/surveys/generate" element={<ProtectedRoute><PageWrapper><SurveyGenerator /></PageWrapper></ProtectedRoute>} />
            <Route path="/surveys/management" element={<ProtectedRoute><PageWrapper><SurveyManagement /></PageWrapper></ProtectedRoute>} />
            <Route path="/surveys/analytics" element={<ProtectedRoute><PageWrapper><AnalyticsDashboard /></PageWrapper></ProtectedRoute>} />
            <Route path="/workshop" element={<ProtectedRoute><PageWrapper><WorkshopFrameworkPage /></PageWrapper></ProtectedRoute>} />
            <Route path="/workshop/silvia-prototypes" element={<ProtectedRoute><PageWrapper><SilviaWorkshopPrototypes /></PageWrapper></ProtectedRoute>} />
            <Route path="/workshop/:workshopId" element={<ProtectedRoute><RouteThemeWrapper><VisualWorkshopPage /></RouteThemeWrapper></ProtectedRoute>} />
            <Route path="/swiss-theme-demo" element={<ProtectedRoute><RouteThemeWrapper><SwissThemeDemo /></RouteThemeWrapper></ProtectedRoute>} />
            */}

            {/* Wireframe Prototype Routes - Crisis Response Dashboard */}
            <Route path="/wireframes" element={<ProtectedRoute><WireframeWrapper><WireframeOverview /></WireframeWrapper></ProtectedRoute>} />
            <Route path="/wireframes/detection" element={<ProtectedRoute><WireframeWrapper><WireframeDetection /></WireframeWrapper></ProtectedRoute>} />
            <Route path="/wireframes/analysis" element={<ProtectedRoute><WireframeWrapper><WireframeAnalysis /></WireframeWrapper></ProtectedRoute>} />
            <Route path="/wireframes/operations" element={<ProtectedRoute><WireframeWrapper><WireframeOperations /></WireframeWrapper></ProtectedRoute>} />
            <Route path="/wireframes/cash" element={<ProtectedRoute><WireframeWrapper><WireframeCashAssistance /></WireframeWrapper></ProtectedRoute>} />
            <Route path="/wireframes/knowledge" element={<ProtectedRoute><WireframeWrapper><WireframeKnowledge /></WireframeWrapper></ProtectedRoute>} />
            <Route path="/wireframes/map" element={<ProtectedRoute><WireframeWrapper><WireframeCrisisMap /></WireframeWrapper></ProtectedRoute>} />
            <Route path="/wireframes/presentation" element={<ProtectedRoute><WireframeWrapper><WireframePresentation /></WireframeWrapper></ProtectedRoute>} />
            </Routes>
          </AppLayout>
        </Router>
      </OnboardingProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;